/** @format */

import { useCallback, useEffect, useState } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import ErrorMessage from '../../Shared/Components/ErrorMessage';
import { error } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { isValidIranianNationalCode } from '../../Shared/utils/helper';
import ClubImageUploader from './ClubImageUploader';

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required('نام باشگاه الزامی است')
    .max(190, 'نام باشگاه نمی‌تواند بیشتر از ۱۹۰ کاراکتر باشد'),
  address: Yup.string()
    .required('آدرس الزامی است')
    .max(1000, 'آدرس نمی‌تواند بیشتر از ۱۰۰۰ کاراکتر باشد'),
  phone: Yup.string()
    .required('شماره تلفن الزامی است')
    .max(20, 'شماره تلفن معتبر نیست'),
  province_id: Yup.number()
    .typeError('استان الزامی است')
    .required('استان الزامی است'),
  city_id: Yup.number()
    .typeError('شهر الزامی است')
    .required('شهر الزامی است'),
  coach_national_code: Yup.string()
    .required('کد ملی مربی الزامی است')
    .length(10, 'کد ملی باید ۱۰ رقم باشد')
    .test('valid-national-code', 'کد ملی معتبر نیست', (value) =>
      value ? isValidIranianNationalCode(value) : false,
    ),
});

const ClubForm = ({
  initialValues,
  existingImages = [],
  onSubmit,
  submitLoading,
  submitLabel = 'ثبت باشگاه',
}) => {
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [coachInfo, setCoachInfo] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [imageState, setImageState] = useState({
    files: [],
    removeImageIds: [],
    filledCount: 0,
    slotCount: 2,
  });

  const handleImageChange = useCallback((state) => {
    setImageState(state);
  }, []);

  useEffect(() => {
    useApi.get(
      '/user/portal/provinces',
      {},
      (res) => setProvinces(res.data.data ?? []),
      () => error('دریافت استان‌ها با خطا مواجه شد'),
    );
  }, []);

  const loadCities = async (provinceId) => {
    if (!provinceId) {
      setCities([]);
      return;
    }

    await useApi.get(
      `/user/portal/cities/${provinceId}`,
      {},
      (res) => setCities(res.data.data ?? []),
      () => error('دریافت شهرها با خطا مواجه شد'),
    );
  };

  useEffect(() => {
    if (initialValues?.province_id) {
      loadCities(initialValues.province_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.province_id]);

  useEffect(() => {
    if (
      initialValues?.coach_national_code &&
      initialValues.coach_national_code.length === 10
    ) {
      searchCoach(initialValues.coach_national_code);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.coach_national_code]);

  const searchCoach = async (nationalCode) => {
    if (!nationalCode || nationalCode.length !== 10) {
      return;
    }

    setCoachLoading(true);
    await useApi.post(
      '/panel/club/search-coach',
      { national_code: nationalCode },
      (res) => {
        setCoachInfo(res.data.data);
        setCoachLoading(false);
      },
      (err) => {
        setCoachInfo(null);
        setCoachLoading(false);
        error(err?.data?.message || 'مربی یافت نشد یا حق عضویت فعال ندارد');
      },
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values) => {
        if (!coachInfo) {
          error('لطفاً ابتدا مربی را با کد ملی جستجو و تأیید کنید');
          return;
        }

        if (imageState.filledCount < 2) {
          error('حداقل ۲ تصویر برای باشگاه الزامی است');
          return;
        }

        if (imageState.filledCount > 6) {
          error('حداکثر ۶ تصویر مجاز است');
          return;
        }

        onSubmit({
          values,
          files: imageState.files,
          removeImageIds: imageState.removeImageIds,
        });
      }}
    >
      {({ values, errors, handleChange, handleSubmit, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          <div className='row g-3'>
            <div className='col-md-6'>
              <label className='f-13 mb-1'>نام باشگاه</label>
              <input
                name='title'
                value={values.title}
                onChange={handleChange}
                className='form-control'
                placeholder='نام باشگاه را وارد کنید'
              />
              <ErrorMessage error={errors.title} />
            </div>

            <div className='col-md-6'>
              <label className='f-13 mb-1'>شماره تلفن</label>
              <input
                name='phone'
                value={values.phone}
                onChange={handleChange}
                className='form-control'
                placeholder='مثال: 02112345678'
              />
              <ErrorMessage error={errors.phone} />
            </div>

            <div className='col-md-6'>
              <label className='f-13 mb-1'>استان</label>
              <select
                name='province_id'
                value={values.province_id}
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue('city_id', '');
                  loadCities(e.target.value);
                }}
                className='form-control'
              >
                <option value=''>انتخاب استان</option>
                {provinces.map((province) => (
                  <option key={province.id} value={province.id}>
                    {province.name}
                  </option>
                ))}
              </select>
              <ErrorMessage error={errors.province_id} />
            </div>

            <div className='col-md-6'>
              <label className='f-13 mb-1'>شهر</label>
              <select
                name='city_id'
                value={values.city_id}
                onChange={handleChange}
                className='form-control'
              >
                <option value=''>انتخاب شهر</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
              <ErrorMessage error={errors.city_id} />
            </div>

            <div className='col-12'>
              <label className='f-13 mb-1'>آدرس</label>
              <textarea
                name='address'
                value={values.address}
                onChange={handleChange}
                className='form-control'
                rows={3}
                placeholder='آدرس کامل باشگاه'
              />
              <ErrorMessage error={errors.address} />
            </div>

            <div className='col-12'>
              <label className='f-13 mb-1'>کد ملی مربی</label>
              <div className='d-flex gap-2'>
                <input
                  name='coach_national_code'
                  value={values.coach_national_code}
                  onChange={(e) => {
                    handleChange(e);
                    setCoachInfo(null);
                  }}
                  className='form-control'
                  maxLength={10}
                  placeholder='کد ملی ۱۰ رقمی مربی'
                />
                <button
                  type='button'
                  className='btn btn-outline-primary btn-sm px-3'
                  onClick={() => searchCoach(values.coach_national_code)}
                  disabled={coachLoading}
                >
                  {coachLoading ? '...' : 'جستجو'}
                </button>
              </div>
              <ErrorMessage error={errors.coach_national_code} />
              {coachInfo && (
                <div className='alert alert-success f-13 mt-2 mb-0 py-2'>
                  <i className='fad fa-user-check ms-1'></i>
                  {coachInfo.full_name} — کد ملی: {coachInfo.national_card_id}
                  <span className='badge bg-success ms-2'>حق عضویت فعال</span>
                </div>
              )}
            </div>

            <div className='col-12'>
              <ClubImageUploader
                existingImages={existingImages}
                onChange={handleImageChange}
              />
            </div>

            <div className='col-12 d-flex justify-content-end'>
              <button
                type='submit'
                className='btn btn-info px-4'
                disabled={submitLoading}
              >
                {submitLoading ? (
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                  />
                ) : (
                  submitLabel
                )}
              </button>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default ClubForm;
